import {
  WebAuthnMode,
  createRpClient,
  toPasskeyTransport,
  toWebAuthnCredential,
  type WebAuthnCredential,
} from "@circle-fin/modular-wallets-core";
import { base64UrlToBytes, bytesToCryptoKey, cryptoKeyToBytes, parseCredentialPublicKey, parsePublicKey, serializePublicKey } from "webauthn-p256";
import { formatCode, isWalletCode } from "./codes";

// app 1.3.22 — the app runs on one origin, globalmoneyapp.com, which is also
// Circle's passkey domain and the client key's web domain. (1.3.21 briefly
// picked a second key by hostname for the Related-Origin test; Circle's RP
// verifier refused the cross-origin assertion, so one origin it is. See
// linkOrigin.ts for how mdrop.it still works.)
const clientKey = import.meta.env.VITE_CLIENT_KEY;
const clientUrl = import.meta.env.VITE_CLIENT_URL;

if (!clientKey || !clientUrl) {
  // Fail loud in dev — a missing client key produces confusing WebAuthn errors.
  console.warn(
    "[M] VITE_CLIENT_KEY / VITE_CLIENT_URL not set. Copy app/.env.example to app/.env.",
  );
}

let _transport: ReturnType<typeof toPasskeyTransport> | null = null;
function transport() {
  return (_transport ??= toPasskeyTransport(clientUrl, clientKey));
}

/**
 * Warm the connection to Circle's passkey endpoint on screen load, so the
 * login challenge fetch (which runs before the Face ID prompt) doesn't pay for
 * DNS + TLS setup at tap time. Safe to call more than once.
 */
let _prewarmed = false;
export function prewarmPasskey() {
  if (_prewarmed) return;
  _prewarmed = true;
  try {
    if (!clientUrl) return;
    const origin = new URL(clientUrl).origin;
    const link = document.createElement("link");
    link.rel = "preconnect";
    link.href = origin;
    link.crossOrigin = "anonymous";
    document.head.appendChild(link);
    // Also instantiate the transport now so any client setup is done early.
    transport();
  } catch {
    /* best-effort warmup */
  }
}

/**
 * REGISTER (new user). `username` is REQUIRED and must be unique + charset-safe
 * on Circle's side — it is the wallet code. v0.60.14 (S11): the label the
 * passkey list shows is a DIFFERENT string, set client-side.
 *
 * How: Circle's RP server builds the WebAuthn creation options from `username`
 * (`user.name`/`user.displayName` = username); the attestation it later
 * verifies carries the challenge, origin and public key — not the label — so we
 * rewrite `user.name`/`user.displayName` before `navigator.credentials.create`.
 * Circle keeps the code as the account username; iOS shows "M® Wallet · CODE".
 * Everything else mirrors the SDK's own startRegistration byte for byte.
 * Grandfathered passkeys keep the label they were created with (WebAuthn can't
 * rename).
 */
/** base64url without padding (WebAuthn's encoding for a user handle). */
export function bytesToBase64Url(b: Uint8Array): string {
  let s = "";
  for (const x of b) s += String.fromCharCode(x);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function passkeyLabel(username: string, displayName?: string | null): string {
  // v0.70.12 — "What shall we call you?" is asked before the ceremony, so the
  // passkey list can carry the person's own name (WebAuthn can't rename later).
  const n = (displayName ?? "").replace(/\s+/g, " ").trim();
  if (n) return `M® · ${n}`;
  return isWalletCode(username) ? `M® Wallet · ${formatCode(username)}` : "M® Wallet";
}

export async function registerCredential(username: string, displayName?: string | null): Promise<WebAuthnCredential> {
  if (typeof window === "undefined" || !window.navigator?.credentials) {
    throw new Error("Credential Management API not supported.");
  }
  const client = createRpClient({ transport: transport() });
  const opts = await client.getRegistrationOptions({ username });
  const label = passkeyLabel(username, displayName);
  const toBuf = (b64: string): ArrayBuffer => {
    const u8 = base64UrlToBytes(b64);
    return u8.buffer.slice(u8.byteOffset, u8.byteOffset + u8.byteLength) as ArrayBuffer;
  };
  const publicKey: PublicKeyCredentialCreationOptions = {
    ...opts,
    challenge: toBuf(opts.challenge),
    user: { ...opts.user, id: toBuf(opts.user.id), name: label, displayName: label },
  };
  const credential = (await window.navigator.credentials.create({ publicKey })) as PublicKeyCredential | null;
  if (!credential) throw new Error("No credential created.");
  await client.getRegistrationVerification({ credential });
  try {
    const pub = await parseCredentialPublicKey(
      (credential.response as AuthenticatorAttestationResponse).getPublicKey()!,
    );
    return {
      id: credential.id,
      publicKey: serializePublicKey(pub, { compressed: true }),
      raw: credential,
      rpId: opts.rp.id,
      userHandle: opts.user.id, // 1.5.44 — Circle's handle for this passkey, as base64url
    } as WebAuthnCredential;
  } catch (error) {
    throw new Error("Credential creation failed.", { cause: error });
  }
}

/**
 * LOGIN (returning user).
 * v1.1.6 — the login contract (design-playbook, *The login contract*):
 *  - `pin` = the credential id of the wallet THIS browser is bound to
 *    (`m.cred`). The request then allows that one passkey only, so Face ID
 *    goes straight to it — no picker — and the emoji on the screen is, by
 *    construction, the wallet being opened.
 *  - no pin = the open, discoverable ceremony: Apple's sheet lists every M®
 *    passkey on the device ("Use another wallet", or no wallet bound here).
 * Either way Circle verifies the full assertion and returns the public key;
 * the address is derived from it. The id alone is never trusted.
 */
export async function loginCredential(pin?: string) {
  return loginPrepared(pin);
}
/** The SDK's own one-call login, kept for reference / emergencies. */
export async function loginCredentialSdk(credentialId?: string) {
  return toWebAuthnCredential({ transport: transport(), mode: WebAuthnMode.Login, credentialId });
}

// --- v1.1.5 — prepare before the tap ---------------------------------------
// The SDK's login is one call that fetches a challenge from Circle and THEN
// asks for the passkey: the tap paid for that round trip before Face ID could
// appear. As on the Send card (v0.90.29), the network work runs while the
// screen is read: `prepareLogin()` fetches the login options on screen load
// (and again when the page comes back), and the tap goes straight to
// `credentials.get`. A prepared challenge is trusted for PREPARED_TTL_MS; a
// stale or missing one is fetched at the tap, as before. If Circle refuses a
// prepared challenge at verification (it was consumed or expired), the login
// is retried once with a fresh one — a second Face ID, but never a dead end.
type LoginOptions = Awaited<ReturnType<ReturnType<typeof createRpClient>["getLoginOptions"]>>;
const PREPARED_TTL_MS = 90_000;
let prepared: { opts: Promise<LoginOptions>; at: number } | null = null;
// v1.1.26 — the login ceremony in flight, so a create can cancel it first.
// WebKit refuses a new WebAuthn ceremony while another is outstanding (the
// "Set up a new wallet" failure in Firefox on iPhone, 4 Oct: NotAllowedError
// with no passkey sheet), so the create path calls abortLogin() before it asks.
let loginAbort: AbortController | null = null;
export function abortLogin(): void {
  try { loginAbort?.abort(); } catch { /* already settled */ }
  loginAbort = null;
  prepared = null; // a challenge prepared for a login has no use in a create
}
export function prepareLogin(): void {
  if (prepared && Date.now() - prepared.at < PREPARED_TTL_MS / 2) return;
  try {
    const client = createRpClient({ transport: transport() });
    const opts = client.getLoginOptions({ userId: "" });
    opts.catch(() => { prepared = null; }); // a failed fetch is forgotten, not kept
    prepared = { opts, at: Date.now() };
  } catch {
    prepared = null;
  }
}
async function takeLoginOptions(): Promise<{ opts: LoginOptions; fresh: boolean }> {
  const p = prepared;
  prepared = null;
  if (p && Date.now() - p.at < PREPARED_TTL_MS) {
    try { return { opts: await p.opts, fresh: false }; } catch { /* fall through */ }
  }
  const client = createRpClient({ transport: transport() });
  return { opts: await client.getLoginOptions({ userId: "" }), fresh: true };
}
async function loginPrepared(pin?: string): Promise<WebAuthnCredential> {
  if (typeof window === "undefined" || !window.navigator?.credentials) {
    throw new Error("Credential Management API not supported.");
  }
  const client = createRpClient({ transport: transport() });
  const toBuf = (b64: string): ArrayBuffer => {
    const u8 = base64UrlToBytes(b64);
    return u8.buffer.slice(u8.byteOffset, u8.byteOffset + u8.byteLength) as ArrayBuffer;
  };
  const attempt = async (opts: LoginOptions) => {
    const publicKey: PublicKeyCredentialRequestOptions = {
      ...opts,
      challenge: toBuf(opts.challenge),
      // Pinned: this browser's wallet only (set here, not by Circle's options,
      // so it doesn't depend on how Circle reads `userId`). Open: as Circle sent.
      allowCredentials: pin
        ? [{ type: "public-key", id: toBuf(pin) }]
        : opts.allowCredentials?.map((c) => ({ ...c, id: toBuf(c.id) })),
    } as PublicKeyCredentialRequestOptions;
    loginAbort = typeof AbortController !== "undefined" ? new AbortController() : null;
    const credential = (await window.navigator.credentials.get({ publicKey, signal: loginAbort?.signal })) as PublicKeyCredential | null;
    loginAbort = null;
    if (!credential) throw new Error("No credential available.");
    const { publicKey: base64Url } = await client.getLoginVerification({ credential });
    if (!base64Url) throw new Error("Login verification failed.");
    const bytes = base64UrlToBytes(base64Url);
    const pub = parsePublicKey(await cryptoKeyToBytes(await bytesToCryptoKey(bytes)));
    // 1.5.44 — a passkey answers with its user handle at every login (it is discoverable), so an
    // old passkey's handle is known from its next login on: what renaming it in Passwords needs.
    const uh = (credential.response as AuthenticatorAssertionResponse).userHandle;
    return {
      id: credential.id,
      publicKey: serializePublicKey(pub, { compressed: true }),
      raw: credential,
      rpId: opts.rpId,
      userHandle: uh && uh.byteLength ? bytesToBase64Url(new Uint8Array(uh)) : undefined,
    } as WebAuthnCredential;
  };
  const first = await takeLoginOptions();
  try {
    return await attempt(first.opts);
  } catch (e) {
    // A prepared challenge Circle no longer accepts: once more, fresh. A
    // cancelled Face ID is not that (NotAllowedError) and is not retried.
    const name = (e as { name?: string })?.name;
    if (first.fresh || name === "NotAllowedError" || name === "AbortError") throw e;
    const again = await client.getLoginOptions({ userId: "" });
    return attempt(again);
  }
}
